import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import get from 'lodash.get';

import { PaginatedResponse } from '~shared/models';
import { ErrorResponse } from '~shared/modules/error-handler';
import { OrderHistoryItem } from '../../models';
import { OrderReturnsService } from '../../services/order-returns.service';
import { OrdersHistoryService } from '../../services/orders-history.service';
import { OrderHistoryAlertService } from '../../services/order-history-alert.service';

import {
  cancelOrder,
  cancelOrderLine,
  cancelOrderLineReturnRequest,
  orderHistoryFailure,
  loadOrder,
  loadOrdersList,
  loadOrdersListSuccess,
  loadOrderSuccess,
  returnOrder,
  returnOrderLineRequest,
  cancelReturnOrder,
} from './orders-history-list.actions';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { OrderLineGroupUtils } from '../../utils';

@Injectable()
export class OrdersHistoryListEffects {
  loadOrdersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrdersList),
      withLatestFrom(
        this.accountInfoService.getAccountInfo(),
        this.accountInfoService.getPerosnalAccountInfo()),
      map(([payload, accountInfo, accountInfoPersonal]) => {
        const { params } = payload;
        const context = get(accountInfoPersonal, 'activeContext');
        const activeContext = context ? context
          : get(accountInfo, 'activeContext');

        return {
          ...params,
          ...(activeContext ? { filter: { activeContext } } : {}),
        };
      }),
      switchMap((params) => {
        return this.ordersService.fetchOrders(params).pipe(
          map((data: PaginatedResponse<OrderHistoryItem>) => {
            data.items = OrderLineGroupUtils.mapVasOrderLines(data.items)
            return loadOrdersListSuccess(data);
          }
          ),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  loadOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrder),
      withLatestFrom(this.accountInfoService.getAccountInfo(),
        this.accountInfoService.getPerosnalAccountInfo()),
      map(([payload, accountInfo, accountInfoPersonal]) => {
        const { orderId } = payload;
        const context = get(accountInfoPersonal, 'activeContext');
        const activeContext = context ? context
          : get(accountInfo, 'activeContext');

        return {
          params: {
            filter: {
              orderId,
              ...(activeContext ? { activeContext } : {}),
            },
          },
          orderId,
        };
      }),
      switchMap(({ orderId, params }) => {
        return this.ordersService.fetchOrders(params).pipe(
          map((orders: PaginatedResponse<OrderHistoryItem>) => orders.items[0]),
          map((order: OrderHistoryItem) =>
            loadOrderSuccess({
              order: {
                id: orderId, changes:
                  OrderLineGroupUtils.mapVasOrderLine(order)
              },
            })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  cancelOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelOrder),
      switchMap(({ orderId }) => {
        return this.ordersService.cancelOrder(orderId).pipe(
          map(() => loadOrder({ orderId })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  returnOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(returnOrder),
      switchMap(({ orderId, orderLineIds, returnReason }) => {
        return this.returnsService.returnOrder(orderId, orderLineIds, returnReason).pipe(
          map(() => loadOrder({ orderId })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  cancelReturnOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelReturnOrder),
      switchMap(({ orderId, orderLineIds }) => {
        return this.returnsService.cancelReturnOrder(orderId, orderLineIds).pipe(
          map(() => loadOrder({ orderId })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  cancelOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelOrderLine),
      switchMap(({ payload: { orderId, orderLineId } }) => {
        return this.ordersService.cancelProduct(orderLineId).pipe(
          map(() => loadOrder({ orderId })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  cancelOrderLineReturnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelOrderLineReturnRequest),
      switchMap(({ payload: { orderLineId, orderId } }) => {
        return this.returnsService.cancelReturnProduct(orderId, orderLineId).pipe(
          map(() => loadOrder({ orderId })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  returnOrderLineRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(returnOrderLineRequest),
      switchMap(({ payload }) => {
        return this.returnsService.returnProduct(payload).pipe(
          map(() => loadOrder({ orderId: payload.orderId })),
          catchError((error: ErrorResponse) => of(orderHistoryFailure({ error }))),
        );
      }),
    ),
  );

  handleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(orderHistoryFailure),
        tap(({ error }) => {
          this.orderHistoryAlertService.openErrorAlert(error);
        }),
      ),
    { dispatch: false },
  );


  constructor(
    private actions$: Actions,
    private ordersService: OrdersHistoryService,
    private returnsService: OrderReturnsService,
    private orderHistoryAlertService: OrderHistoryAlertService,
    private accountInfoService: AccountInfoService,
  ) {
  }
}
